import { createElement } from '../../../core/dom/helpers.js';
import { createLogger } from '../../../core/logging/logger.js';

const log = createLogger({ module: 'file-driver' });

/**
 * @typedef {import('../../../contracts/upload.js').ExistingUpload} ExistingUpload
 * @typedef {import('../../../contracts/file-selection.js').FileMeta} FileMeta
 */

/**
 * Shared session/abort/error machinery for the file-field driver — the cross-mode
 * concerns that `mountSingle` and `mountMulti` both need, extracted so neither copies
 * them. Each mount keeps its OWN DOM scaffold, preview/tile rendering, value model
 * (single value vs array) and accept-gate; this helper owns only:
 *
 * - busy/disabled state (`forcedDisabled`/`uploading`/`activeSession`/`uploadAbort`/
 *   `destroyed`) + `applyDisabled()` over the caller's controls,
 * - the single freshness predicate (`isStale()` / `isCurrent(controller)`),
 * - the library-picker open dance (`openPicker`), and
 * - the native upload(+persist) lifecycle (`runNativeUpload`), incl. abort safety.
 *
 * The per-mode bits stay in the caller via two callbacks on `runNativeUpload`:
 * `produce({ signal, isCurrent })` (the upload+map → ExistingUpload(s)) and
 * `commit(value)` (the render + onChange). Picker results are mapped/committed by the
 * caller too — `openPicker` just resolves the raw `FileMeta[]`.
 *
 * @param {Object} args
 * @param {HTMLElement} args.wrapper - The driver root (its `.hidden` factors into staleness).
 * @param {import('../../../contracts/forms.js').FileFieldDef} args.def - The field def (`def.disabled` factors into staleness/disabled).
 * @param {import('../../../contracts/forms.js').FieldDriverContext} args.ctx - Driver ctx (reads `ctx.filePicker`).
 * @param {Array<HTMLElement | null | undefined>} args.buttons - Controls `applyDisabled()` toggles (nulls skipped).
 * @param {(disabled: boolean) => void} [args.onDisabledChange] - Called from INSIDE `applyDisabled()` on
 *   every disabled-state transition (internal: upload start/end, picker open/close; external:
 *   `setForcedDisabled`). The photo-card driver uses this to fan disabled state into its dynamic cards
 *   (which aren't in `buttons`). Receives the same computed `disabled` value used for the buttons.
 */
export function createFileDriverSession({ wrapper, def, ctx, buttons, onDisabledChange }) {
  let forcedDisabled = false;
  let uploading = false;
  let destroyed = false;
  /** Picker session controller (the in-editor modal). */
  /** @type {AbortController | null} */
  let activeSession = null;
  /** Native upload(+persist) controller — independent of the picker session. */
  /** @type {AbortController | null} */
  let uploadAbort = null;

  // Error node: created here, NOT auto-appended — the caller appends `errorEl` in the
  // exact position its markup uses today (last), so the rendered DOM is unchanged.
  const errorEl = createElement('div', { class: 'file-driver__error', role: 'alert', 'aria-live': 'polite' });
  errorEl.hidden = true;

  /** @param {string} msg */
  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }
  function clearError() {
    if (errorEl.hidden) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  /** The current disabled/busy predicate. Single source of truth — computed once, used everywhere. */
  function isDisabled() {
    return forcedDisabled || Boolean(def.disabled) || uploading || activeSession !== null;
  }

  function applyDisabled() {
    const disabled = isDisabled();
    for (const btn of buttons) {
      if (btn) /** @type {HTMLButtonElement | HTMLInputElement} */ (btn).disabled = disabled;
    }
    // Same computed value to the dynamic-card observer (no recomputation → no drift).
    onDisabledChange?.(disabled);
  }

  /**
   * The single freshness predicate: a torn-down / hidden / disabled / force-disabled
   * field must not commit. The async paths add their own controller identity/abort
   * checks via `isCurrent`.
   */
  function isStale() {
    return destroyed || forcedDisabled || Boolean(def.disabled) || wrapper.hidden;
  }

  /**
   * Whether direct user interaction (remove tile, drag-reorder) should be blocked —
   * i.e. the field is force-disabled, def-disabled, or mid-upload. (Distinct from
   * `isStale`, which also covers hidden/destroyed for async-commit guarding.)
   */
  function isInteractionBlocked() {
    return forcedDisabled || Boolean(def.disabled) || uploading;
  }

  /** @param {AbortController} controller */
  function isCurrentUpload(controller) {
    return controller === uploadAbort && !controller.signal.aborted && !isStale();
  }

  /** Close any open picker (abort its session). No-op when none open. */
  function closePicker() {
    if (activeSession) {
      activeSession.abort();
      activeSession = null;
      applyDisabled();
    }
  }

  /** Abort an in-flight native upload/persist (no stale commit, no orphan row). */
  function abortNativeUpload() {
    if (uploadAbort) {
      uploadAbort.abort();
      uploadAbort = null;
      // We tore down the in-flight upload here, so its `.finally` (gated on
      // `controller === uploadAbort`) will NOT run — reset the busy state now,
      // else the field stays permanently disabled after a later re-show/enable.
      uploading = false;
      applyDisabled();
    }
  }

  /**
   * Open the in-editor library picker and resolve the chosen files. Runs the
   * session-identity dance + post-await freshness guard; the caller maps/commits the
   * result (single takes `[0]`, multi appends all). Resolves `null` on cancel / abort /
   * stale / missing capability. Non-abort failures are logged (same message as today).
   *
   * @param {{ multiple: boolean }} opts
   * @returns {Promise<FileMeta[] | null>}
   */
  async function openPicker({ multiple }) {
    if (!ctx.filePicker) return null;
    const controller = new AbortController();
    activeSession = controller;
    applyDisabled();
    try {
      const { promise } = ctx.filePicker.open({
        accept: def.accept ?? '',
        multiple,
        signal: controller.signal,
      });
      const result = await promise;
      if (controller !== activeSession || controller.signal.aborted || isStale()) return null;
      return result;
    } catch (err) {
      log.error('Library picker failed', { data: err });
      return null;
    } finally {
      if (controller === activeSession) {
        activeSession = null;
        applyDisabled();
      }
    }
  }

  /**
   * Run the native upload(+persist) lifecycle. The session owns the controller,
   * busy/disabled bookkeeping, the `.finally` reset, and the post-await freshness gate;
   * the per-mode `produce`/`commit` keep the mapping out of here. `produce` receives the
   * abort `signal` (flows through upload AND persist) and `isCurrent()` (the explicit
   * mid-flight guard a multi-step `produce` must check BEFORE persist, to avoid an orphan
   * `/files` row). Non-abort failures are logged (same message as today).
   *
   * @template {ExistingUpload | ExistingUpload[]} V
   * @param {Object} args
   * @param {(args: { signal: AbortSignal, isCurrent: () => boolean }) => Promise<V | null>} args.produce
   * @param {(value: V) => void} args.commit
   */
  function runNativeUpload({ produce, commit }) {
    const controller = new AbortController();
    uploadAbort = controller;
    uploading = true;
    applyDisabled();
    Promise.resolve()
      .then(() => produce({ signal: controller.signal, isCurrent: () => isCurrentUpload(controller) }))
      .then((value) => {
        // Post-await freshness gate (stale/torn-down/hidden/disabled/superseded → no
        // commit). A `null` from `produce` (its own pre-persist guard tripped) also
        // short-circuits.
        if (value == null || !isCurrentUpload(controller)) return;
        commit(value);
      })
      .catch((/** @type {unknown} */ err) => {
        if (controller.signal.aborted) return;
        log.error('File upload failed', { data: err });
      })
      .finally(() => {
        if (controller === uploadAbort) {
          uploadAbort = null;
          uploading = false;
          applyDisabled();
        }
      });
  }

  return {
    errorEl,
    showError,
    clearError,
    applyDisabled,
    isStale,
    isInteractionBlocked,
    isDisabled,
    openPicker,
    runNativeUpload,
    closePicker,
    abortNativeUpload,
    /** @param {boolean} v */
    setForcedDisabled(v) { forcedDisabled = v; },
    markDestroyed() { destroyed = true; },
  };
}
